import { useState } from 'react';
import { LuChevronDown, LuChevronUp, LuSlidersHorizontal } from 'react-icons/lu';
import Checkbox from '@/components/Checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  createEmptyPanelIds,
  FILTER_PANEL_DEFINITIONS,
  USER_FILTER_PANEL_BY_TARGET,
} from './filterPanelDefinitions';
import type {
  AnalysisFilterPopoverProps,
  FilterPanelKey,
  FilterOption,
  FilterTargetValue,
} from './types';

type SelectedIdsByPanelParams = {
  filterTarget: FilterTargetValue;
  filterOrganizationUserIds: number[];
  filterCompanyTagIds: number[];
  filterJobTagIds: number[];
  filterCandidateTagIds: number[];
};

const createSelectedIdsByPanel = ({
  filterTarget,
  filterOrganizationUserIds,
  filterCompanyTagIds,
  filterJobTagIds,
  filterCandidateTagIds,
}: SelectedIdsByPanelParams): Record<FilterPanelKey, number[]> => ({
  ...createEmptyPanelIds(),
  [USER_FILTER_PANEL_BY_TARGET[filterTarget]]: filterOrganizationUserIds,
  company_tag: filterCompanyTagIds,
  job_tag: filterJobTagIds,
  candidate_tag: filterCandidateTagIds,
});

/**
 * 分析画面の複合フィルターPopover。
 * 担当者系の対象切り替えと、タグ系フィルターの一時選択をまとめて扱う。
 * availablePanels で表示するパネルを呼び出し側から絞り込める（インサイトタブ用途）。
 */
export default function AnalysisFilterPopover({
  organizationUsers,
  filterOrganizationUserIds,
  filterTarget,
  companyTags,
  filterCompanyTagIds,
  jobTags,
  filterJobTagIds,
  candidateTags,
  filterCandidateTagIds,
  onFilterChange,
  availablePanels,
}: AnalysisFilterPopoverProps) {
  const panels = availablePanels ?? FILTER_PANEL_DEFINITIONS;
  const [isOpen, setIsOpen] = useState(false);
  const [localPanelKey, setLocalPanelKey] = useState<FilterPanelKey>(
    USER_FILTER_PANEL_BY_TARGET[filterTarget]
  );
  const [localFilterTarget, setLocalFilterTarget] = useState<FilterTargetValue>(filterTarget);
  const [localSelectedIdsByPanel, setLocalSelectedIdsByPanel] = useState<
    Record<FilterPanelKey, number[]>
  >(() =>
    createSelectedIdsByPanel({
      filterTarget,
      filterOrganizationUserIds,
      filterCompanyTagIds,
      filterJobTagIds,
      filterCandidateTagIds,
    })
  );
  const selectedCount =
    filterOrganizationUserIds.length +
    filterCompanyTagIds.length +
    filterJobTagIds.length +
    filterCandidateTagIds.length;
  const panelOptions: Record<FilterPanelKey, FilterOption[]> = {
    candidate_user: organizationUsers,
    company_user: organizationUsers,
    company_tag: companyTags,
    job_tag: jobTags,
    candidate_tag: candidateTags,
  };
  const selectedIdsByPanel = createSelectedIdsByPanel({
    filterTarget,
    filterOrganizationUserIds,
    filterCompanyTagIds,
    filterJobTagIds,
    filterCandidateTagIds,
  });
  const selectedPanelDefinition = panels.find(({ key }) => selectedIdsByPanel[key].length > 0);
  const selectedFilterLabel =
    selectedPanelDefinition && selectedCount > 0
      ? `${selectedPanelDefinition.label}（${selectedCount}）`
      : 'フィルター';

  const resetLocalState = () => {
    setLocalPanelKey(USER_FILTER_PANEL_BY_TARGET[filterTarget]);
    setLocalFilterTarget(filterTarget);
    setLocalSelectedIdsByPanel(
      createSelectedIdsByPanel({
        filterTarget,
        filterOrganizationUserIds,
        filterCompanyTagIds,
        filterJobTagIds,
        filterCandidateTagIds,
      })
    );
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      resetLocalState();
    }
    setIsOpen(open);
  };

  const handlePanelChange = (key: FilterPanelKey) => {
    setLocalPanelKey(key);
    const definition = panels.find((item) => item.key === key);
    if (definition?.filterTarget) {
      setLocalFilterTarget(definition.filterTarget);
    }
  };

  const handleToggle = (id: number, checked: boolean) => {
    const toggleIds = (currentIds: number[]) =>
      checked ? [...new Set([...currentIds, id])] : currentIds.filter((v) => v !== id);

    setLocalSelectedIdsByPanel((prev) => {
      const nextSelectedIdsByPanel = checked ? createEmptyPanelIds() : { ...prev };

      return {
        ...nextSelectedIdsByPanel,
        [localPanelKey]: toggleIds(prev[localPanelKey]),
      };
    });
  };

  const handleApply = () => {
    const organizationUserPanelKey = USER_FILTER_PANEL_BY_TARGET[localFilterTarget];
    onFilterChange({
      filterTarget: localFilterTarget,
      organizationUserIds: localSelectedIdsByPanel[organizationUserPanelKey],
      companyTagIds: localSelectedIdsByPanel.company_tag,
      jobTagIds: localSelectedIdsByPanel.job_tag,
      candidateTagIds: localSelectedIdsByPanel.candidate_tag,
    });
    setIsOpen(false);
  };

  const handleCancel = () => {
    resetLocalState();
    setIsOpen(false);
  };

  const handleClear = () => {
    setLocalSelectedIdsByPanel(createEmptyPanelIds());
    onFilterChange({
      filterTarget: localFilterTarget,
      organizationUserIds: [],
      companyTagIds: [],
      jobTagIds: [],
      candidateTagIds: [],
    });
    setIsOpen(false);
  };

  const localSelectedCount = Object.values(localSelectedIdsByPanel).reduce(
    (count, ids) => count + ids.length,
    0
  );
  const currentOptions = panelOptions[localPanelKey];
  const currentSelectedIds = localSelectedIdsByPanel[localPanelKey];

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-expanded={isOpen}
          className={`flex h-[42px] w-fit min-w-[180px] items-center gap-1 rounded border border-solid bg-white px-2 py-1.5 text-sm transition-colors ${
            isOpen || selectedCount > 0
              ? 'border-primary text-primary'
              : 'border-border text-main hover:border-primary'
          }`}
        >
          <LuSlidersHorizontal className="size-5 shrink-0" />
          <span className="text-left whitespace-nowrap">{selectedFilterLabel}</span>
          {isOpen ? (
            <LuChevronUp className="size-5 shrink-0" />
          ) : (
            <LuChevronDown className="size-5 shrink-0" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={2}
        className="w-[643px] max-w-[calc(100vw-24px)] overflow-hidden rounded border border-solid border-border p-0 shadow-[0px_0px_6px_0px_rgba(0,0,0,0.2)]"
      >
        <div className="flex h-[322px] bg-white">
          <div
            role="tablist"
            aria-label="フィルター対象"
            className="w-[210px] shrink-0 border-y border-r border-solid border-border bg-white px-2 py-[9px]"
          >
            {panels.map((option) => {
              const active = option.key === localPanelKey;

              return (
                <button
                  key={option.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => handlePanelChange(option.key)}
                  className={`flex w-full items-center px-3 py-2 text-left text-sm leading-[1.7] ${
                    active
                      ? 'bg-primary_lighten text-primary'
                      : 'bg-white text-main hover:bg-gray-50'
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>

          <div className="flex min-w-0 flex-1 flex-col bg-white px-4 py-2">
            <div className="min-h-0 flex-1 overflow-y-auto">
              <ul className="w-[388px]">
                {currentOptions.map((option) => (
                  <li key={option.id} className="flex items-center px-3 py-2">
                    <Checkbox
                      checked={currentSelectedIds.includes(option.id)}
                      onChange={(checked) => handleToggle(option.id, checked)}
                    >
                      <span className="max-w-[300px] truncate text-sm leading-[1.7] font-normal text-main">
                        {option.name}
                      </span>
                    </Checkbox>
                  </li>
                ))}
                {currentOptions.length === 0 && (
                  <li className="px-3 py-2 text-sm text-sub">選択できる項目がありません</li>
                )}
              </ul>
            </div>
          </div>
        </div>

        <div className="flex items-start justify-end gap-2 border-t border-solid border-border bg-white px-4 py-2">
          {localSelectedCount > 0 && (
            <button
              type="button"
              onClick={handleClear}
              className="mr-auto flex h-8 items-center justify-center rounded border border-solid border-border bg-white px-2 text-xs font-bold text-main hover:bg-gray-50"
            >
              クリア
            </button>
          )}
          <button
            type="button"
            onClick={handleCancel}
            className="flex h-8 items-center justify-center rounded border border-solid border-border bg-white px-2 text-xs font-bold text-main hover:bg-gray-50"
          >
            キャンセル
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="flex h-8 items-center justify-center rounded bg-primary px-2 text-xs font-bold text-white hover:opacity-90"
          >
            決定
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
